/**
 * CUÁNDO SE CELEBRA UNA SUBIDA DE NIVEL, fuera del efecto.
 *
 * Vivía dentro de un `useEffect` con una consulta a IndexedDB en medio, así que
 * la única forma de probar el caso que importa —la cuenta recién hecha— era
 * hacerse una cuenta. Y ese caso es el que se rompe: no hay nada guardado, el
 * nivel general ya vale 1, y una comparación ingenua le suelta un «¡has subido
 * al nivel 1!» a alguien que no ha hecho absolutamente nada.
 */

export type Celebracion = {
  /** Qué anotar como «visto». `null` = no tocar lo guardado. */
  anotar: number | null;
  /** De dónde venías, o `null` si no hay nada que celebrar. */
  desde: number | null;
};

/**
 * `visto` llega de `meta`, que es un almacén de clave/valor sin tipos: puede ser
 * un número, puede no existir, y puede ser basura de una versión anterior. Por
 * eso entra como `unknown` y se comprueba aquí en vez de confiar en un `as`.
 *
 * Tres casos y solo tres:
 *
 *  · NO HAY NADA GUARDADO → se anota y se calla. Es la primera vez que esta
 *    persona abre la app en este dispositivo; no ha subido de nivel, es que
 *    acaba de llegar.
 *  · SUBIÓ → se celebra y se anota.
 *  · IGUAL O MENOR → ni se celebra ni se anota. Que baje no debería pasar
 *    —`levelFloor` es un suelo sellado— pero si pasara, rebajar lo anotado
 *    haría que al recuperar el nivel se celebrara otra vez algo ya celebrado.
 */
export function celebracionDeNivel(visto: unknown, actual: number, silencio = false): Celebracion {
  if (typeof visto !== 'number' || !Number.isFinite(visto)) {
    return { anotar: actual, desde: null };
  }
  // `silencio`: la primera vuelta tras la descarga inicial. Se anota lo que hay
  // —sin bajar lo anotado— y no se celebra: el nivel de hace meses no es noticia.
  if (actual > visto) return { anotar: actual, desde: silencio ? null : visto };
  return { anotar: null, desde: null };
}
